import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import find from 'lodash/find';

import { SuiContainer, SuiDropdown, SuiMenuItem } from '@splashthat/sui';
import ChevronDownIcon from 'common/icons/ChevronDown';
import MultilineDropdownOption from './MultilineDropdownOption';
import WorkspaceTopbarTitle from '../WorkspaceTopbarTitle';

const BASE_CLASS_NAME = 'WorkspaceTopbarDropdown';

const getCurrentLabelFromOptions = (options, currentValue) => get(find(options, { value: currentValue }), 'label');

/**
 * Renders a Workspace Title that behaves like a dropdown.
 * It works by rendering a hidden dropdown, and then opening
 * it on click of the Workspace Title container
 */

const WorkspaceTopbarDropdown = (props) => {
    const dropdown = React.createRef();
    const [currentLabel, setCurrentLabel] = useState(getCurrentLabelFromOptions(props.options, props.value));

    const openHiddenDropdown = (e) => {
        const selectElement = get(dropdown, 'current.select.current');
        if (selectElement) {
            selectElement.click();
        }
    };

    // Update the current label when value changes
    useEffect(() => {
        setCurrentLabel(getCurrentLabelFromOptions(props.options, props.value));
    }, [props.value, props.options]);

    return (
        <SuiContainer className={BASE_CLASS_NAME} variation="flex-vertical full-height">
            <SuiDropdown
                className={`${BASE_CLASS_NAME}__hidden-dropdown`}
                value={props.value}
                onChange={props.onChange}
                ref={dropdown}
                disabled={props.disabled}
                data-qa={props.dataQa}
            >
                {props.options.map((option) => (
                    <SuiMenuItem
                        key={option.value}
                        value={option.value}
                        displayValue={option.label}
                        data-qa={`${BASE_CLASS_NAME}__option-${option.value}`}
                        label={
                            <MultilineDropdownOption
                                label={option.label}
                                helperText={option.helperText}
                                isFeatured={!!option.isFeatured}
                            />
                        }
                    />
                ))}
            </SuiDropdown>
            <SuiContainer
                className={`${BASE_CLASS_NAME}__fake-dropdown`}
                onClick={openHiddenDropdown}
                variation="flex-horizontal full-height"
            >
                <WorkspaceTopbarTitle title={currentLabel} subtitle={props.subtitle} />
                <SuiContainer>
                    <ChevronDownIcon />
                </SuiContainer>
            </SuiContainer>
        </SuiContainer>
    );
};

WorkspaceTopbarDropdown.propTypes = {
    subtitle: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            helperText: PropTypes.string,
            isFeatured: PropTypes.bool,
        }),
    ).isRequired,
    disabled: PropTypes.bool,
    dataQa: PropTypes.string,
};

WorkspaceTopbarDropdown.defaultProps = {
    subtitle: null,
    disabled: false,
    dataQa: BASE_CLASS_NAME,
};

export default WorkspaceTopbarDropdown;
